import React from 'react';
import { EasyImage } from '../Tools/EasyImage';
import Icon, { IconType } from '../Icon/Icon';
import { AwardRank, ISlideInfo, IWinnerInfo } from './SlideShowTypes';

interface IProps {
  slide: ISlideInfo;
  active?: boolean;
  revealed?: boolean;
  thumb?: boolean;
  onClick?: () => void;
}

const rankLabel: Record<AwardRank, string> = {
  first: '1st Place',
  second: '2nd Place',
  third: '3rd Place',
  honor: 'Award',
};

const WinnerBlock = (props: { winner: IWinnerInfo; revealed: boolean; compact?: boolean }) => {
  const { winner, revealed, compact } = props;
  const rank = winner.Rank || 'honor';
  return (
    <div className={`ss-winner ss-rank-${rank} ${revealed ? 'ss-revealed' : 'ss-hidden'}`}>
      <div className={`ss-medal ss-medal-${rank}`}>{rank === 'honor' ? <Icon type={IconType.award} /> : <span>{rank === 'first' ? '1' : rank === 'second' ? '2' : '3'}</span>}</div>
      <div className="ss-winner-body">
        {winner.AwardSubType && <div className="ss-winner-award">{winner.AwardSubType}</div>}
        <div className={`ss-winner-reveal ${revealed ? 'is-on' : ''}`}>
          <div className="ss-winner-name">
            {winner.ProjectId && <span className="ss-pid">{winner.ProjectId}</span>}
            <span>{winner.Title}</span>
          </div>
          {winner.Owners && <div className="ss-winner-owners">{winner.Owners}</div>}
          {(winner.School || winner.Division || winner.Category) && (
            <div className="ss-winner-meta">
              {winner.School}
              {winner.School && (winner.Division || winner.Category) ? ' · ' : ''}
              {winner.Division}
              {winner.Division && winner.Category ? ' · ' : ''}
              {winner.Category}
            </div>
          )}
        </div>
        {!revealed && <div className="ss-drumroll">{compact ? 'Winner' : 'And the winner is...'}</div>}
        {(winner.PresentedBy || winner.Sponsor) && revealed && (
          <div className="ss-presented">
            {winner.SponsorLogo && <EasyImage src={winner.SponsorLogo} alt={winner.Sponsor} className="ss-sponsor-mini" showCloudsOnError={false} />}
            <span>
              {winner.PresentedBy ? `Presented by ${winner.PresentedBy}` : ''}
              {winner.PresentedBy && winner.Sponsor ? ' · ' : ''}
              {winner.Sponsor && !winner.PresentedBy ? `Sponsored by ${winner.Sponsor}` : winner.Sponsor && winner.PresentedBy ? winner.Sponsor : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const Slide = (props: IProps) => {
  const s = props.slide;
  const revealed = props.revealed ?? true;
  const style: React.CSSProperties = {
    backgroundColor: s.BackgroundColor,
    color: s.TextColor,
    ['--ss-accent' as any]: s.AccentColor || 'var(--style-color-primary)',
    ['--ss-text' as any]: s.TextColor || '#f4f1ea',
  };

  return (
    <div
      className={`ss-frame ${props.active ? 'is-active' : ''} ${props.thumb ? 'is-thumb' : ''} ss-kind-${s.Kind}`}
      onClick={() => props.onClick && props.onClick()}
      onDoubleClick={() => props.onClick && props.onClick()}>
      <div className="ss-slide" style={style}>
        {s.BackgroundImageUrl && <div className="ss-bg" style={{ backgroundImage: `url(${s.BackgroundImageUrl})` }} />}
        <div className="ss-scrim" />
        <div className="ss-accent-bar" />
        {(s.LogoUrl || s.FairName) && (
          <div className="ss-brand">
            {s.LogoUrl && <img src={s.LogoUrl} alt="" className="ss-logo" />}
            {s.FairName && <span>{s.FairName}</span>}
          </div>
        )}
        <div className="ss-body">
          {s.Kind === 'title' && (
            <>
              {s.Title && <h1 className="ss-title">{s.Title}</h1>}
              {s.SubTitle && <h2 className="ss-sub">{s.SubTitle}</h2>}
            </>
          )}
          {s.Kind === 'section' && (
            <>
              <div className="ss-kicker">Up next</div>
              {s.Title && <h1 className="ss-title">{s.Title}</h1>}
              {s.SubTitle && <h2 className="ss-sub">{s.SubTitle}</h2>}
            </>
          )}
          {(s.Kind === 'winner' || s.Kind === 'winners') && (
            <>
              {s.Title && <h1 className={`ss-title ${s.Kind === 'winner' ? 'ss-title-sm' : ''}`}>{s.Title}</h1>}
              {s.SubTitle && <h2 className="ss-sub">{s.SubTitle}</h2>}
              {s.Description && <p className="ss-desc">{s.Description}</p>}
              <div className={`ss-winner-list ${s.Kind === 'winners' ? 'is-multi' : ''}`}>
                {s.Winners?.map((w, i) => (
                  <WinnerBlock key={i} winner={w} revealed={revealed} compact={s.Kind === 'winners'} />
                ))}
              </div>
            </>
          )}
          {s.Kind === 'sponsors' && (
            <>
              {s.Title && <h1 className="ss-title ss-title-sm">{s.Title}</h1>}
              <div className="ss-sponsor-grid">
                {s.Sponsors?.map((x, i) => (
                  <div key={i} className="ss-sponsor">
                    {x.logoAddress ? <EasyImage src={x.logoAddress} alt={x.name} showCloudsOnError={true} /> : <h2>{x.name}</h2>}
                    {x.logoAddress && x.name && <div className="ss-sponsor-name">{x.name}</div>}
                  </div>
                ))}
              </div>
            </>
          )}
          {s.Kind === 'thanks' && (
            <>
              {s.Title && <h1 className="ss-title ss-title-sm">{s.Title}</h1>}
              <div className="ss-names">
                {s.Names?.map((n, i) => (
                  <div key={i} className="ss-name">
                    {n}
                  </div>
                ))}
              </div>
            </>
          )}
          {s.Kind === 'custom' && (
            <div className={`ss-custom ${s.ImageUrl ? 'has-image' : ''}`}>
              <div>
                {s.Title && <h1 className="ss-title">{s.Title}</h1>}
                {s.SubTitle && <h2 className="ss-sub">{s.SubTitle}</h2>}
                {s.Description && <p className="ss-desc">{s.Description}</p>}
              </div>
              {s.ImageUrl && <EasyImage src={s.ImageUrl} alt={s.Title} className="ss-custom-image" showCloudsOnError={true} />}
            </div>
          )}
          {s.Kind === 'recap' && (
            <>
              {s.Title && <h1 className="ss-title ss-title-sm">{s.Title}</h1>}
              {s.SubTitle && <h2 className="ss-sub">{s.SubTitle}</h2>}
              {s.RecapUrl && (
                <div className="ss-qr">
                  <img src={`https://qrcode.zfairs.com/qrcode?value=${encodeURIComponent(s.RecapUrl)}`} alt="Recap QR code" />
                  <div className="ss-qr-url">{s.RecapUrl}</div>
                </div>
              )}
            </>
          )}
        </div>
        {s.Kind === 'winner' && s.Winners?.[0]?.Rank && revealed && <div className="ss-rank-tag">{rankLabel[s.Winners[0].Rank]}</div>}
      </div>
    </div>
  );
};

export default Slide;
